/**
 * SKOP Trade Summary Feed
 *
 * Displays AI-generated summaries of recent transactions.
 */

import { useState } from 'react';
import {
  summarizeTransaction,
  getSentimentColor,
  getSignificanceColor,
  type Transaction,
  type TradeSummary,
} from '../lib/trade-summaries';

interface TradeSummaryFeedProps {
  transactions: Transaction[];
  maxItems?: number;
  showRecap?: boolean;
}

export function TradeSummaryFeed({
  transactions,
  maxItems = 10,
  showRecap = true,
}: TradeSummaryFeedProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const recentTxs = transactions.slice(0, maxItems);

  if (recentTxs.length === 0) {
    return (
      <div style={{
        padding: '24px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
      }}>
        <div style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: 'var(--text-tertiary)' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
            <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
          </svg>
        </div>
        <div style={{ fontSize: '13px' }}>No recent transactions</div>
      </div>
    );
  }

  // Calculate recap stats
  const buys = recentTxs.filter((tx) =>
    tx.type === 'buy' || tx.type === 'whale_buy' || tx.type === 'bundled_buy'
  );
  const sells = recentTxs.filter((tx) =>
    tx.type === 'sell' || tx.type === 'whale_sell' || tx.type === 'creator_sell'
  );
  const totalBuyVolume = buys.reduce((sum, tx) => sum + tx.solAmount, 0);
  const totalSellVolume = sells.reduce((sum, tx) => sum + tx.solAmount, 0);
  const netFlow = totalBuyVolume - totalSellVolume;

  return (
    <div>
      {/* Recap Header */}
      {showRecap && (
        <div style={{
          padding: '12px 16px',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '12px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '14px', height: '14px', display: 'inline-flex', color: 'var(--accent)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </span>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Activity Summary</span>
            </div>
            <span style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
            }}>
              Last {recentTxs.length} transactions
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
          }}>
            <MiniStat
              label="Buys"
              value={`${buys.length}`}
              subtext={`${totalBuyVolume.toFixed(2)} SOL`}
              color="var(--success)"
            />
            <MiniStat
              label="Sells"
              value={`${sells.length}`}
              subtext={`${totalSellVolume.toFixed(2)} SOL`}
              color="var(--error)"
            />
            <MiniStat
              label="Net Flow"
              value={`${netFlow >= 0 ? '+' : ''}${netFlow.toFixed(2)}`}
              subtext="SOL"
              color={netFlow >= 0 ? 'var(--success)' : 'var(--error)'}
            />
          </div>
        </div>
      )}

      {/* Transaction Feed */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {recentTxs.map((tx) => {
          const summary = summarizeTransaction(tx);
          const isExpanded = expanded === tx.signature;

          return (
            <div
              key={tx.signature}
              onClick={() => setExpanded(isExpanded ? null : tx.signature)}
              style={{
                padding: '12px',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                borderLeft: `3px solid ${getSentimentColor(summary.sentiment)}`,
                transition: 'background 0.2s',
              }}
            >
              {/* Header Row */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}>
                <TransactionIcon emoji={summary.emoji} sentiment={summary.sentiment} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    marginBottom: '4px',
                  }}>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: getSentimentColor(summary.sentiment),
                    }}>
                      {summary.headline}
                    </span>
                    <SignificanceBadge significance={summary.significance} />
                  </div>

                  {/* Timestamp */}
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--text-tertiary)',
                  }}>
                    {formatTimestamp(tx.timestamp)}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border)',
                }}>
                  <p style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                    margin: '0 0 12px 0',
                  }}>
                    {summary.description}
                  </p>

                  {/* Transaction Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '8px',
                    fontSize: '12px',
                  }}>
                    <DetailRow label="Amount" value={`${tx.solAmount.toFixed(4)} SOL`} />
                    <DetailRow label="Tokens" value={formatTokenAmount(tx.tokenAmount)} />
                    {tx.walletLabel && (
                      <DetailRow label="Wallet" value={tx.walletLabel} />
                    )}
                    {tx.holdingPercentAfter !== undefined && (
                      <DetailRow
                        label="Holdings After"
                        value={`${tx.holdingPercentAfter.toFixed(2)}%`}
                      />
                    )}
                  </div>

                  {/* View on Explorer */}
                  <a
                    href={`https://solscan.io/tx/${tx.signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '12px',
                      fontSize: '12px',
                      color: 'var(--accent)',
                      textDecoration: 'none',
                    }}
                  >
                    View on Solscan →
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TransactionIcon({ emoji, sentiment }: { emoji: string; sentiment: TradeSummary['sentiment'] }) {
  const iconProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

  // Map emoji codes to SVG icons
  const iconMap: Record<string, React.ReactNode> = {
    'whale': (
      <svg {...iconProps}>
        <path d="M3 3v18h18"/>
        <path d="M18 17V9"/>
        <path d="M13 17V5"/>
        <path d="M8 17v-3"/>
      </svg>
    ),
    'alert': (
      <svg {...iconProps}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    'warning': (
      <svg {...iconProps}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    'up': (
      <svg {...iconProps}>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    'down': (
      <svg {...iconProps}>
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
        <polyline points="17 18 23 18 23 12"/>
      </svg>
    ),
    'target': (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="6"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    'money': (
      <svg {...iconProps}>
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    'graduation': (
      <svg {...iconProps}>
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
      </svg>
    ),
    'send': (
      <svg {...iconProps}>
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
      </svg>
    ),
    'water': (
      <svg {...iconProps}>
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
      </svg>
    ),
    'eye': (
      <svg {...iconProps}>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    ),
    'default': (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  };

  // Map emoji to icon key
  const getIconKey = (e: string): string => {
    if (e.includes('whale') || e === '\uD83D\uDC0B') return 'whale';
    if (e.includes('alert') || e === '\uD83D\uDEA8') return 'alert';
    if (e.includes('warning') || e === '\u26A0\uFE0F') return 'warning';
    if (e.includes('up') || e === '\uD83D\uDCC8') return 'up';
    if (e.includes('down') || e === '\uD83D\uDCC9') return 'down';
    if (e.includes('target') || e === '\uD83C\uDFAF') return 'target';
    if (e.includes('money') || e === '\uD83D\uDCB0') return 'money';
    if (e.includes('graduation') || e === '\uD83C\uDF93') return 'graduation';
    if (e.includes('send') || e === '\uD83D\uDCE4') return 'send';
    if (e.includes('water') || e === '\uD83D\uDCA7' || e === '\uD83D\uDEB0') return 'water';
    if (e.includes('eye') || e === '\uD83D\uDC40') return 'eye';
    return 'default';
  };

  const iconKey = getIconKey(emoji);

  return (
    <span style={{ width: '18px', height: '18px', display: 'inline-flex', color: getSentimentColor(sentiment) }}>
      {iconMap[iconKey] || iconMap['default']}
    </span>
  );
}

function MiniStat({
  label,
  value,
  subtext,
  color,
}: {
  label: string;
  value: string;
  subtext: string;
  color: string;
}) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{ fontSize: '16px', fontWeight: 600, color }}>
        {value}
      </div>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
        {subtext}
      </div>
    </div>
  );
}

function SignificanceBadge({ significance }: { significance: TradeSummary['significance'] }) {
  if (significance === 'low') return null;

  const labels = {
    medium: 'Notable',
    high: 'Important',
    critical: 'Critical',
  };

  return (
    <span style={{
      padding: '2px 6px',
      fontSize: '10px',
      fontWeight: 600,
      background: `${getSignificanceColor(significance)}20`,
      color: getSignificanceColor(significance),
      borderRadius: 'var(--radius-sm)',
      textTransform: 'uppercase',
    }}>
      {labels[significance]}
    </span>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function formatTimestamp(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTokenAmount(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(2)}B`;
  }
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(2)}K`;
  }
  return amount.toFixed(2);
}
